/**
 * Call Slice - Manages active call sessions and call state
 */

import type { SoftphoneStore } from 'Modules/softphone/store';
import { resolveNextCurrentCallId } from 'Modules/softphone/utils/callState';
import { StateCreator } from 'zustand';
import { CallSlice } from './index.types';

export * from './index.types';

export const createCallSlice: StateCreator<
  SoftphoneStore,
  [],
  [],
  CallSlice
> = (set, get) => ({
  // State
  activeCalls: new Map(),
  incomingCalls: new Map(),
  currentCallId: null,

  // Actions
  addCall: (call) =>
    set((state) => {
      // Reset poor connection tooltip flag for new call
      const updates: Partial<SoftphoneStore> = {
        activeCalls: new Map(state.activeCalls).set(call.id, call),
        hasPoorTooltipShown: false,
      };

      return updates;
    }),

  updateCall: (callId, updates) =>
    set((state) => {
      const call = state.activeCalls.get(callId);
      if (!call) {
        return state;
      }

      const updatedCall = { ...call, ...updates };

      return {
        activeCalls: new Map(state.activeCalls).set(callId, updatedCall),
      };
    }),

  removeCall: (callId) =>
    set((state) => {
      const newCalls = new Map(state.activeCalls);
      newCalls.delete(callId);

      // If the removed call was the current one, promote the next held call (Transfer use case)
      const newCurrentCallId =
        state.currentCallId === callId
          ? resolveNextCurrentCallId(newCalls)
          : state.currentCallId;

      // If no more active calls, reset keypad, dialpad and transfer state
      const shouldReset = newCalls.size === 0;

      // Always reset transfer state when the call involved in the transfer ends
      const isTransferCallEnding =
        callId === state.currentCallId && state.isTransferActive;

      return {
        activeCalls: newCalls,
        currentCallId: newCurrentCallId,
        // Reset keypad, dialpad and transfer when last call is removed
        ...(shouldReset && {
          isKeypadActive: false,
          isDialpadActive: false,
          keypadDigits: '',
          dialpadDigits: '',
          isTransferActive: false,
          transferStep: 'search',
          transferTarget: null,
          transferSelectedPhoneNumber: null,
          isTransferring: false,
          transferType: 'blind',
          searchTerm: '',
        }),
        // Reset transfer state if the current call (with active transfer) ends
        ...(!shouldReset && isTransferCallEnding && {
          isTransferActive: false,
          transferStep: 'search',
          transferTarget: null,
          transferSelectedPhoneNumber: null,
          isTransferring: false,
          transferType: 'blind',
          searchTerm: '',
        }),
      };
    }),

  setCurrentCall: (callId) => {
    const currentState = get();
    const previousCallId = currentState.currentCallId;

    // Auto-close transfer view when currentCallId changes (Requirement 2.4)
    if (callId !== previousCallId && currentState.isTransferActive) {
      set({ currentCallId: callId, isTransferActive: false });
    } else {
      set({ currentCallId: callId });
    }
  },

  addIncomingCall: (call) =>
    set((state) => ({
      incomingCalls: new Map(state.incomingCalls).set(call.id, call),
    })),

  removeIncomingCall: (callId) =>
    set((state) => {
      const newCalls = new Map(state.incomingCalls);
      newCalls.delete(callId);
      return { incomingCalls: newCalls };
    }),

  clearAllCalls: () =>
    set({
      activeCalls: new Map(),
      incomingCalls: new Map(),
      currentCallId: null,
      // Reset transfer state when all calls are cleared
      isTransferActive: false,
      transferStep: 'search',
      transferTarget: null,
      transferSelectedPhoneNumber: null,
      isTransferring: false,
      transferType: 'blind',
      searchTerm: '',
    }),
});
